/**
 * Domain vocabulary, canonical with docs/agents/domain.md and CONTEXT.md.
 * Only types with a second consumer today live here; more follow in later tickets.
 */

/** Role within a List. Owner > Admin > Editor > Viewer (ADR-0009, glossary). */
export type Role = "owner" | "admin" | "editor" | "viewer";

/**
 * Subscription plan (glossary): Free by default, Paid lifts Quota and
 * adds Embed authoring. Synced into Convex via RevenueCat webhook —
 * clients read the tier from Convex, never from RevenueCat (ADR-0007).
 */
export type Tier = "free" | "paid";

export const ROLE_ORDER: readonly Role[] = ["owner", "admin", "editor", "viewer"];

/**
 * The role rank (ADR-0009 order): true when `role` is at least `min`.
 * Roles arrive as raw wire strings and callers cast at the boundary,
 * so the rank fails closed on a non-Role string itself (#820) — the
 * guard lives HERE, not in every caller's discipline: indexOf answers
 * -1 for anything outside ROLE_ORDER, and -1 compares as the smallest
 * rank, which read garbage as Owner. A garbage `min` already compares
 * false through the arithmetic (rank <= -1 is never true), so only the
 * `role` side needs the door.
 */
export function roleAtLeast(role: Role, min: Role): boolean {
  if (!ROLE_ORDER.includes(role)) return false;
  return ROLE_ORDER.indexOf(role) <= ROLE_ORDER.indexOf(min);
}

/**
 * The role-change capability matrix (ticket #399), the client-side twin
 * of the server's members:setRole rule: an admin-or-above caller manages
 * non-owner members who are not themselves; an admin (non-owner) caller
 * can neither manage nor be handed Admin — only the Owner grants Admin.
 * Both platforms consume this one home: mobile's member role sheet and,
 * since #659, web's Members panel — whose behavioral suite
 * (apps/web/src/membersPanel.test.tsx) holds the matrix through the web
 * wiring itself, successor to the retired shared-side parity pin.
 */
export function canChangeMemberRole(
  callerRole: Role,
  targetRole: Role,
  isSelf: boolean,
): boolean {
  if (isSelf) return false;
  // Roles arrive as raw wire strings. The rank now refuses a non-Role
  // CALLER itself (#820), but the matrix's own arms would still admit
  // a garbage TARGET — an owner caller passes roleAtLeast, and a
  // garbage target matches neither the owner exclusion nor the
  // admin/admin one — so both sides refuse at the door.
  if (!ROLE_ORDER.includes(callerRole) || !ROLE_ORDER.includes(targetRole)) {
    return false;
  }
  if (!roleAtLeast(callerRole, "admin")) return false;
  if (targetRole === "owner") return false;
  if (callerRole === "admin" && targetRole === "admin") return false;
  return true;
}

/** Roles a Member can hold through the role sheet — ownership never moves through it. */
const MANAGEABLE_ROLES: readonly Exclude<Role, "owner">[] = [
  "viewer",
  "editor",
  "admin",
];

/**
 * The role options a caller may OFFER in the role sheet (ticket #399):
 * the Owner additionally offers Admin; everyone else offers
 * Viewer/Editor only.
 */
export function roleOptionsFor(
  callerRole: Role,
): readonly Exclude<Role, "owner">[] {
  return callerRole === "owner"
    ? MANAGEABLE_ROLES
    : MANAGEABLE_ROLES.filter((role) => role !== "admin");
}

/** Display label for a Role — the capitalized invite-toggle vocabulary. */
export function roleLabel(role: Role): string {
  return role.charAt(0).toUpperCase() + role.slice(1);
}

/**
 * Member colours: every Member of a List gets a distinct colour (glossary).
 * Assigned at Join by picking the first palette colour not in use (ticket #14
 * reuses this server-side); the pure logic lives here so web and mobile agree.
 */
export const MEMBER_COLORS = [
  "#E4572E", // rust
  "#2E86AB", // sea blue
  "#4C9F70", // leaf
  "#B07ACC", // violet
  "#D9A404", // mustard
  "#D966B3", // pink
  "#0FB9B1", // teal
  "#E07B39", // amber
  "#5A6ACF", // indigo
  "#8A8D3A", // olive
] as const;

export type MemberColor = (typeof MEMBER_COLORS)[number];

export function nextAvailableColor(colorsInUse: readonly string[]): MemberColor {
  const used = new Set(colorsInUse);
  for (const c of MEMBER_COLORS) {
    if (!used.has(c)) return c;
  }
  // Palette exhausted: fall back to deterministic repeat (list larger than palette).
  return MEMBER_COLORS[colorsInUse.length % MEMBER_COLORS.length]!;
}

/**
 * Title attribution colour (tickets #12 + #201): edits by OTHER Members
 * render in the editor's per-List colour; the VIEWER's own edits render
 * in ink (null) — a solo List is one person's writing, and the viewer's
 * Member colour (first in the palette: rust) must not read as an error
 * state or collide with the rose delete affordances. Done rows keep
 * their muted struck-through style regardless of attribution. A viewer
 * whose membership has not settled also reads as ink — unknown never
 * paints. Web's ItemRow and mobile's BulletLine both consume this one
 * rule — no platform drift.
 */
export function attributionColor({
  editorColor,
  done,
  viewerMemberId,
  lastEditorMemberId,
}: {
  editorColor: string | null | undefined;
  done: boolean;
  viewerMemberId?: string;
  lastEditorMemberId?: string;
}): string | null {
  if (done || viewerMemberId === undefined) return null;
  if (lastEditorMemberId === viewerMemberId) return null;
  return editorColor ?? null;
}

/**
 * Auth redirect policy, canonical for web and mobile (ADR-0006 + ADR-0011).
 * Mobile OAuth bounces back into the app via deep link; everything else
 * keeps the Convex Auth default: relative paths resolve against SITE_URL,
 * same-origin absolutes pass, anything else is rejected. The deep-link
 * check runs first so it never depends on SITE_URL being set; every other
 * redirect does (the library requires the SITE_URL env var).
 */
export const DEEP_LINK_PREFIX = "todotogether://";

export function resolveAuthRedirect(redirectTo: string, siteUrl: string): string {
  if (redirectTo.startsWith(DEEP_LINK_PREFIX)) {
    return redirectTo;
  }
  if (siteUrl === "") {
    throw new Error("Missing environment variable SITE_URL");
  }
  const base = siteUrl.replace(/\/$/, "");
  if (redirectTo.startsWith("?") || redirectTo.startsWith("/")) {
    return `${base}${redirectTo}`;
  }
  if (redirectTo.startsWith(base)) {
    const after = redirectTo[base.length];
    if (after === undefined || after === "?" || after === "/") {
      return redirectTo;
    }
  }
  throw new Error(
    `Invalid \`redirectTo\` ${redirectTo} for configured SITE_URL: ${base}`,
  );
}

/**
 * The URL the mobile OAuth browser session redirects back to. The Convex
 * callback appends the sign-in code as a query param (ADR-0011).
 */
export function authDeepLinkUrl(): string {
  return `${DEEP_LINK_PREFIX}auth`;
}

/**
 * Pull the sign-in `code` out of a deep-link URL the OAuth flow landed on
 * (todotogether://auth?code=…). Returns null for any other URL.
 */
export function extractAuthCode(url: string): string | null {
  if (!url.startsWith(DEEP_LINK_PREFIX)) return null;
  const queryStart = url.indexOf("?");
  if (queryStart === -1) return null;
  const params = new URLSearchParams(url.slice(queryStart + 1));
  return params.get("code");
}

/**
 * Invite deep link for `token` — the app-side counterpart of the web
 * /invite/:token URL (ticket #22). The shareable link stays the web URL;
 * the web invite page offers this scheme link as an "open in the app"
 * handoff, and Android/iOS app-link registration can reuse it later.
 */
export function inviteDeepLinkUrl(token: string): string {
  return `${DEEP_LINK_PREFIX}invite/${encodeURIComponent(token)}`;
}

/**
 * The canonical web origin (ticket #25 domain, #168 straggler sweep): the
 * bought domain, fronted by Cloudflare Pages. todotogether.pages.dev keeps
 * working as an alias (same Pages deployment), so old share links never
 * break — but everything NEW points here.
 */
export const CANONICAL_WEB_ORIGIN = "https://todotogether.app";

/**
 * The bare host of the canonical origin, derived from it (ticket #204):
 * what user-facing copy shows and what splitOnWebOrigin matches. Every
 * mention of the site in copy reads this, so a future domain flip
 * updates the words automatically — never a second literal.
 */
export const CANONICAL_WEB_HOST = CANONICAL_WEB_ORIGIN.replace(
  /^https?:\/\//,
  "",
);

/**
 * One piece of a display string split around the canonical host.
 * `web-origin` segments carry no URL — what a tap means is the
 * consumer's business (the mobile auth hint opens the verify screen
 * keyed to the address the user typed; the web policy page links the
 * origin itself, #202).
 */
export type WebOriginSegment =
  | { kind: "text"; text: string }
  | { kind: "web-origin"; text: string };

/**
 * Split a display string into plain-text and web-origin segments
 * (ticket #202's shared piece; consumers: the mobile auth hint #204
 * and the web privacy policy #202). The host is matched as derived from
 * CANONICAL_WEB_ORIGIN — never a second literal — and only as a
 * STANDALONE host: an occurrence that extends into a longer hostname
 * on either side (notodotogether.app, todotogether.app.evil.com) is
 * left plain text, extractInviteToken's prefix-collision safety at
 * text level. A lone trailing dot is sentence punctuation, not a
 * subdomain, so "visit todotogether.app." still matches. Segments
 * always re-join to the input.
 */
export function splitOnWebOrigin(display: string): WebOriginSegment[] {
  const segments: WebOriginSegment[] = [];
  let emittedUpTo = 0;
  let scanFrom = 0;
  for (;;) {
    const at = display.indexOf(CANONICAL_WEB_HOST, scanFrom);
    if (at === -1) break;
    scanFrom = at + 1;
    const end = at + CANONICAL_WEB_HOST.length;
    if (!standaloneHost(display, at, end)) continue;
    if (at > emittedUpTo) {
      segments.push({ kind: "text", text: display.slice(emittedUpTo, at) });
    }
    segments.push({ kind: "web-origin", text: CANONICAL_WEB_HOST });
    emittedUpTo = end;
  }
  if (emittedUpTo < display.length) {
    segments.push({ kind: "text", text: display.slice(emittedUpTo) });
  }
  return segments;
}

/** Characters whose presence flush against the host make it a longer
 * hostname rather than a mention of ours. */
const HOSTNAME_GLUE = /[\w.-]/;

/** True when the occurrence at [start, end) is the whole host: no
 * hostname character glued to either side, and any following dot is
 * punctuation (nothing nameable after it). */
function standaloneHost(display: string, start: number, end: number): boolean {
  if (start > 0 && HOSTNAME_GLUE.test(display[start - 1]!)) return false;
  if (end >= display.length) return true;
  const after = display[end]!;
  if (after !== "." && HOSTNAME_GLUE.test(after)) return false;
  if (after === ".") {
    const next = display[end + 1];
    return next === undefined || !HOSTNAME_GLUE.test(next);
  }
  return true;
}

/**
 * The shareable invite URL (web landing page) — single source for the
 * host so mobile shares and the web fallback can't drift apart.
 */
export function inviteWebUrl(token: string): string {
  return `${CANONICAL_WEB_ORIGIN}/invite/${encodeURIComponent(token)}`;
}

/**
 * The password-reset email's link (ticket #174): the reset form with the
 * code AND the address it belongs to in the query, so clicking it opens
 * the form ready for just a new password. Both values are needed — the
 * reset-verification flow is keyed to the address, and the link only ever
 * travels inside that address's own mailbox. The reset form reads
 * ?code= and ?email= the same way it reads ?invite= (#160's mechanism).
 */
export function resetWebUrl(token: string, email: string): string {
  return `${CANONICAL_WEB_ORIGIN}/reset?email=${encodeURIComponent(email)}&code=${encodeURIComponent(token)}`;
}

/**
 * The email-verification email's link (ticket #192, the reset mail's
 * #174 pattern): the verify form with the code AND the address it
 * belongs to in the query, so clicking it opens the form with the code
 * already in place — one explicit Verify click from verified. Both
 * values are needed — the email-verification flow is keyed to the
 * address, and the link only ever travels inside that address's own
 * mailbox. The verify form reads ?code= beside the ?email= it already
 * read (#126's screen).
 */
export function verifyWebUrl(token: string, email: string): string {
  return `${CANONICAL_WEB_ORIGIN}/verify?email=${encodeURIComponent(email)}&code=${encodeURIComponent(token)}`;
}

/**
 * The verify screen keyed to the address alone (ticket #204): the mobile
 * AuthScreen error message opens it for the address the user typed when
 * an error names the canonical host (rendered through splitOnWebOrigin).
 * The code is deliberately absent — on mobile the user carries it in
 * their own mailbox, and the web screen reads a bare ?email= as "a code
 * was just sent to this address" (#126), landing on the code-entry step.
 */
export function verifyWebUrlForEmail(email: string): string {
  return `${CANONICAL_WEB_ORIGIN}/verify?email=${encodeURIComponent(email)}`;
}

/**
 * Pull the invite token out of a deep link (todotogether://invite/…) or a
 * web share URL on a known app host (the canonical domain, prod Pages,
 * local dev) that an installer may have opened in-app — arbitrary hosts
 * are rejected so a random page can't smuggle tokens into the Join flow.
 */
export function extractInviteToken(url: string): string | null {
  const scheme = url.match(/^todotogether:\/\/invite\/([^/?#]+)/);
  if (scheme) return decodeURIComponent(scheme[1]!);
  const web = url.match(
    /^https?:\/\/(?:todotogether\.app|todotogether\.pages\.dev|localhost(?::\d+)?)\/invite\/([^/?#]+)/,
  );
  if (web) return decodeURIComponent(web[1]!);
  return null;
}

/**
 * The minimal Member shape the display label needs (web's Member and the
 * mobile member row both carry it — glossary "removed member").
 */
export type MemberLike = {
  name: string | null;
  email: string | null;
  role: string;
  removedAt?: number;
};

/**
 * Display label for a Member row/assignee. A removed row whose user is
 * gone (account deleted, ticket #16) has no name or email left — history
 * must not render a bare role ghost.
 */
export function memberLabel(m: MemberLike | undefined | null): string | null {
  if (!m) return null;
  if (!m.name && !m.email) {
    return m.removedAt !== undefined ? "removed member" : m.role;
  }
  return m.name || m.email || m.role;
}

/**
 * Sentence-start capitalisation for committed titles (ticket #205): an
 * Item title or List name typed in lowercase comes back with its first
 * letter uppercased, so "buy milk" commits as "Buy milk". Deliberately
 * narrow, so it can never mangle deliberate casing:
 *  - only a leading ASCII lowercase letter is touched — "3 apples",
 *    "😊 hello", already-capital text and leading whitespace pass
 *    through unchanged;
 *  - a first word that already carries an uppercase letter beyond its
 *    opening character was capitalised deliberately ("iPhone case",
 *    "eBay wins") and is left alone; the check is first-word-scoped so
 *    "buy iPhone case" still capitalises;
 *  - non-ASCII openers ("éclair") are never rewritten — a blanket
 *    toUpperCase can change length ("ß" -> "SS") and the ASCII rule
 *    keeps the transform length-stable;
 *  - alphanumeric tokens capitalise like every platform keyboard does
 *    ("e2e test" -> "E2e test") — only a genuine leading uppercase
 *    (camel/brand casing) is treated as deliberate.
 * Callers trim first: the server stores the trimmed title, and the
 * capital is decided on the same shape.
 */
export function capitaliseFirstLetter(text: string): string {
  const firstWord = text.split(/\s/, 1)[0] ?? "";
  if (/[A-Z]/.test(firstWord.slice(1))) return text;
  return text.replace(/^[a-z]/, (c) => c.toUpperCase());
}

/**
 * Next orderKey after `last` without importing the server's engine — a
 * lexicographic suffix is always strictly greater, good enough for an
 * optimistic placeholder until the server's real key arrives (ticket #12;
 * shared with mobile, ticket #22).
 */
export function orderKeyAfter(last: string | null): string {
  return `${last ?? ""}~`;
}

/**
 * Due dates: an Item's optional dueAt is a UTC-midnight epoch ms number
 * (date-only semantics — clients convert a `YYYY-MM-DD` input with
 * `dueDateToMs`). dueInfo turns it into the badge the row and the
 * lists-screen "Next up" panel render; the pure logic lives here so web
 * and mobile agree, and so it is unit-testable against a pinned `now`.
 */
export type DueTone = "overdue" | "today" | "soon" | "later";
export type DueInfo = { tone: DueTone; label: string };

const DAY_MS = 86_400_000;
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;
const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
] as const;

const utcDay = (ms: number): number => Math.floor(ms / DAY_MS);

function formatDay(d: Date, now: number): string {
  const sameYear = d.getUTCFullYear() === new Date(now).getUTCFullYear();
  return sameYear
    ? `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`
    : `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

export function dueInfo(dueAt: number, now: number = Date.now()): DueInfo {
  const diff = utcDay(dueAt) - utcDay(now);
  if (diff < 0) {
    return { tone: "overdue", label: formatDay(new Date(dueAt), now) };
  }
  if (diff === 0) return { tone: "today", label: "Today" };
  if (diff === 1) return { tone: "soon", label: "Tomorrow" };
  const d = new Date(dueAt);
  if (diff <= 6) return { tone: "soon", label: WEEKDAYS[d.getUTCDay()]! };
  return { tone: "later", label: formatDay(d, now) };
}

/** `YYYY-MM-DD` (a date input's value) → UTC-midnight epoch ms. */
export function dueDateToMs(date: string): number | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return null;
  const ms = Date.parse(`${date}T00:00:00Z`);
  return Number.isNaN(ms) ? null : ms;
}

/** UTC-midnight epoch ms → `YYYY-MM-DD` for a date input's value. */
export function dueMsToDate(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

/**
 * N days from now as `YYYY-MM-DD` — UTC-day arithmetic, the input shape
 * of {@link dueDateToMs}. Mobile's quick-set ("Due today"/"Due
 * tomorrow") feeds this straight in, so quick-set and the badge math
 * can never drift apart the way a screen-local reimplementation would.
 */
export function dueDateFromNow(
  days: number,
  now: number = Date.now(),
): string {
  return new Date(now + days * DAY_MS).toISOString().slice(0, 10);
}

/**
 * App-facing List summaries exactly as lists:myLists returns them
 * (issue #10, consumed by the web shell and the mobile Lists screen).
 * `starredAt` (issue #70) is the CALLER's own star stamp — present only
 * when this Member starred the List; never anyone else's.
 * `completedCount`/`totalCount` (issue #271) are LIVE-item counts
 * (soft-deleted items excluded) for the front page's progress fill —
 * one shape, both platforms, so the fraction can never drift.
 * `memberCount` (ticket #753) is the List's LIVE active-member count —
 * departed/kicked memberships never count, the same liveness rule as
 * every access check; the personal/shared classifier (listScope.ts)
 * consumes it.
 */
export type ListSummary = {
  _id: string;
  name: string;
  starredAt?: number;
  completedCount: number;
  totalCount: number;
  memberCount: number;
};
export type JoinedListSummary = ListSummary & { role: Role };

/**
 * The front-page row fill's fraction (issue #271): completed ÷ total,
 * or NULL when the list holds no live items — "0 of 0" must never
 * render as full or complete; null means NO fill at all. Clamped to
 * [0, 1] so a hand-built summary can't overflow the row. Pure here so
 * web and mobile consume the one rule.
 */
export function progressFraction(
  summary: Pick<ListSummary, "completedCount" | "totalCount">,
): number | null {
  if (summary.totalCount <= 0) return null;
  return Math.min(1, Math.max(0, summary.completedCount / summary.totalCount));
}

/**
 * The front-page row fill's WIDTH in px (ticket #363): the fraction
 * against the row's own MEASURED width, not a percentage string. The
 * mobile fill is absolutely positioned inside a padded row, and Yoga
 * resolves an absolute child's "%" against a narrower box than the row
 * it visually tracks — so `width: "100%"` stopped short of the row's
 * right edge at EVERY fraction. Passing the row's onLayout width in
 * makes "all done" reach the right edge by construction, on any Yoga
 * version. Same [0, 1] clamp and same no-live-items rule as
 * progressFraction (null → 0: an unmeasured or item-less row paints
 * nothing rather than guessing full).
 */
export function progressFillWidth(
  summary: Pick<ListSummary, "completedCount" | "totalCount">,
  rowWidth: number,
): number {
  const fraction = progressFraction(summary);
  if (fraction === null) return 0;
  return Math.min(1, Math.max(0, fraction)) * Math.max(0, rowWidth);
}

/**
 * Starred Lists (issue #70): the Lists screen pins starred Lists above
 * unstarred ones, each group keeping its existing relative order — a
 * stable partition on the caller's own `starredAt` stamp. The star is
 * the Member's own view, so the reorder happens client-side on the
 * caller's rows only; the server's row order is untouched.
 */
export function starredFirst<T extends { starredAt?: number }>(
  lists: readonly T[],
): T[] {
  return [
    ...lists.filter((l) => l.starredAt !== undefined),
    ...lists.filter((l) => l.starredAt === undefined),
  ];
}

/**
 * Embed blocks (ticket #19, ADR-0008): the paid-authoring block types that
 * may appear inside an Item's Notes. The names ARE the BlockNote block
 * `type`s stored in the Notes JSON — changing one invalidates stored docs,
 * so web, mobile (#23) and the server's gate must all import from here.
 */
export const EMBED_BLOCK_TYPES = [
  "embedLink",
  "embedImage",
  "embedVideo",
  "embedMap",
] as const;
export type EmbedBlockType = (typeof EMBED_BLOCK_TYPES)[number];

/**
 * The web/mobile upsell hint for the embed insert controls (the
 * FormatBar renders it beside the disabled buttons while unpaid).
 * Client-side only since #415: the server's blanket notes-authoring gate
 * (PAID_NOTES_MESSAGE) subsumed the embed-specific refusal — a Free
 * writer never reaches the server with any doc, embed-bearing or not.
 */
export const PAID_EMBED_MESSAGE =
  "Embeds are a paid feature — upgrade to add links, images, videos and maps.";

/**
 * The notes-authoring gate (#415): ALL Notes authoring is the Paid
 * surface — plain text included — reversing #13's "(ungated)" lineage
 * (per ADR-0008 the paid feature IS block content). The server's gate
 * (items:setNotes) refuses every Free writer with this message right
 * after the role check, and the client upsells (mobile island #416, web
 * panel #417) say the same thing in the same words, like
 * PAID_EMBED_MESSAGE. Viewing stays free for every Member, and lapse
 * re-gates without ever destroying ("re-gates, never destroys").
 */
export const PAID_NOTES_MESSAGE =
  "Notes are a paid feature — upgrade to write and format.";

/**
 * The notes upsell's affordance label (issue #699): the trailing
 * link/button the two hosts supply as the editor core's upgradeAction —
 * the web panel's react-router Link (apps/web/src/Notes.tsx) and the
 * island's bridge button (apps/web/src/notes/Island.tsx) — said in the
 * same words, like PAID_EMBED_MESSAGE. Before #699 the surfaces had
 * drifted ("See plans & upgrade" vs "See plans in Settings"); the
 * unified copy is the web voice, the majority spelling across the
 * upsell surfaces, and accurate on both since both hosts' actions land
 * on the same settings/plans surface. Issue #725 completed the census:
 * the web paywall-upsell (App.tsx), the web export-upsell
 * (ListDetail.tsx) and the native restore-screen CTA
 * (RecentlyDeletedScreen.tsx) render it too — the complete upsell set
 * is held by notesPaywallPins' issue-#725 census. Rendered as a string
 * expression, so the ampersand needs no JSX entity.
 */
export const SEE_PLANS_LINK_LABEL = "See plans & upgrade";

/**
 * The restore gate (tickets #71/#69): Lists and Items soft-deletes are
 * restorable for 30 days, but RESTORE is the Paid capability — Free and
 * lapsed Users are refused with this message. The server's gate
 * (lists:restore / items:restore) and the web upsell hint say the same
 * thing in the same words, like PAID_EMBED_MESSAGE.
 */
export const PAID_RESTORE_MESSAGE =
  "Restore is a paid feature — upgrade to recover deleted items and lists.";

/**
 * The Notes depth cap (#131): Convex refuses to store any document
 * nested more than 16 levels, and a notes block consumes two of those
 * (the block object + its children array), the text-bearing leaf about
 * five. From six levels on, setNotes' write therefore died with an
 * UNCAUGHT server error whose client-facing message dumped the entire
 * item document — so setNotes refuses anything deeper than this BEFORE
 * walking or writing it, with the friendly message below. The cap is
 * exactly the storage engine's envelope: nothing that was ever saveable
 * is refused, and real BlockNote nesting (indented lists/quotes) sits
 * at 2-3.
 *
 * notesExceedDepth is the check; it walks with an explicit stack, since
 * a depth checker that recurses would be refuted by its own subject.
 */
export const MAX_NOTE_DEPTH = 5;

/** setNotes' refusal copy — same words server-side and in any client hint. */
export const NOTES_DEPTH_MESSAGE = `Notes are nested too deeply (max ${MAX_NOTE_DEPTH} levels)`;

export function notesExceedDepth(blocks: unknown): boolean {
  if (!Array.isArray(blocks)) return false;
  const stack: Array<{ node: unknown; depth: number }> = blocks.map(
    (node) => ({ node, depth: 1 }),
  );
  while (stack.length > 0) {
    const { node, depth } = stack.pop()!;
    if (typeof node !== "object" || node === null) continue;
    const children = (node as { children?: unknown }).children;
    if (!Array.isArray(children) || children.length === 0) continue;
    if (depth + 1 > MAX_NOTE_DEPTH) return true;
    for (const child of children) {
      stack.push({ node: child, depth: depth + 1 });
    }
  }
  return false;
}

/** Max bytes for one uploaded Embed image (checked client and server side). */
export const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

export function formatMaxImageBytes(): string {
  return `${Math.floor(MAX_IMAGE_BYTES / (1024 * 1024))}MB`;
}

/**
 * The per-User storage budget for Embed images (ticket #129): a Paid
 * User's finalized uploads may total 500 MB. The free tier is already
 * upload-blocked at the seam, so this ceiling only ever binds Paid.
 */
export const PAID_STORAGE_BUDGET_BYTES = 500 * 1024 * 1024;

export function formatStorageBudgetBytes(): string {
  return `${Math.floor(PAID_STORAGE_BUDGET_BYTES / (1024 * 1024))}MB`;
}

/**
 * The finalize-time refusal copy when an upload would push a User past
 * the storage budget — the server gate says it in these words, like
 * PAID_EMBED_MESSAGE. States the action that actually frees space: the
 * accounting rule (#129) releases an image's bytes as soon as its embed
 * is removed from the Item's notes (or the Item is permanently deleted).
 */
export const PAID_STORAGE_MESSAGE =
  "You've used all 500 MB of image storage. Removing images frees space.";

/**
 * Whether accepting `incomingBytes` on top of `usageBytes` would push a
 * User past the budget. Landing EXACTLY at the budget is allowed; the
 * first byte beyond is refused (boundary pinned by test).
 */
export function wouldExceedStorageBudget(
  usageBytes: number,
  incomingBytes: number,
): boolean {
  return usageBytes + incomingBytes > PAID_STORAGE_BUDGET_BYTES;
}

/**
 * A pasted link validated for an embedLink card: only http(s) — anything
 * else (javascript:, data:, garbage) is rejected rather than stored.
 */
export function parseLinkTarget(raw: string): { url: string; domain: string } | null {
  let parsed: URL;
  try {
    parsed = new URL(raw.trim());
  } catch {
    return null;
  }
  if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return null;
  return { url: parsed.toString(), domain: parsed.hostname };
}

/**
 * A pasted video URL reduced to a privacy-friendly iframe src. Videos are
 * LINKS ONLY (ADR-0008: never raw uploads) — YouTube and Vimeo today;
 * anything else returns null and the insert form refuses it.
 */
export type VideoEmbed = { provider: "youtube" | "vimeo"; embedUrl: string };

export function parseVideoUrl(raw: string): VideoEmbed | null {
  const input = raw.trim();
  let url: URL;
  try {
    url = new URL(input);
  } catch {
    return null;
  }
  const id = /^[A-Za-z0-9_-]+$/;
  if (/^(?:www\.)?youtube\.com$/.test(url.hostname)) {
    const v = url.searchParams.get("v");
    if (url.pathname === "/watch" && v && id.test(v)) {
      return { provider: "youtube", embedUrl: `https://www.youtube-nocookie.com/embed/${v}` };
    }
    const shorts = url.pathname.match(/^\/(?:shorts|embed|live)\/([A-Za-z0-9_-]+)/);
    if (shorts) {
      return { provider: "youtube", embedUrl: `https://www.youtube-nocookie.com/embed/${shorts[1]}` };
    }
    return null;
  }
  if (url.hostname === "youtu.be") {
    const v = url.pathname.slice(1);
    if (v && id.test(v)) {
      return { provider: "youtube", embedUrl: `https://www.youtube-nocookie.com/embed/${v}` };
    }
    return null;
  }
  const vimeo = url.hostname.match(/^(?:www\.)?vimeo\.com$/);
  if (vimeo) {
    const v = url.pathname.match(/^\/(\d+)(?:\/([A-Za-z0-9]+))?/);
    if (v) {
      return {
        provider: "vimeo",
        embedUrl: `https://player.vimeo.com/video/${v[1]}${v[2] ? `?h=${v[2]}` : ""}`,
      };
    }
    return null;
  }
  return null;
}

/**
 * A pasted map target — bare "lat,lon", or coordinates lifted from a
 * shared Google/OSM link (@lat,lonz, ?q=lat,lon, ?mlat=..&mlon=..,
 * /#map=z/lat/lon). Parsing happens ONCE at insert; the stored block keeps
 * the numbers so rendering never needs a geocoder.
 */
export type MapTarget = { lat: number; lon: number };

const COORD = "(-?\\d+(?:\\.\\d+)?)";

/** The first lat/lon pair we accept out of a pasted target, by shape. */
const MAP_PATTERNS: RegExp[] = [
  // bare "48.8584,2.2945"
  new RegExp(`^\\s*${COORD}[,\\s/]+${COORD}\\s*$`),
  // Google Maps: …/@48.8584,2.2945,17z…
  new RegExp(`@${COORD},${COORD}`),
  // Google Maps search: …?q=48.8584,2.2945
  new RegExp(`[?&]q=${COORD},${COORD}`),
  // OSM marker: …?mlat=48.8584&mlon=2.2945
  new RegExp(`[?&]mlat=${COORD}&[?&]?mlon=${COORD}`),
  // OSM position: …/#map=15/48.8584/2.2945
  new RegExp(`#map=\\d+(?:\\.\\d+)?/${COORD}/${COORD}`),
  // last resort: the first coordinate-looking pair anywhere
  new RegExp(`${COORD}[,\\s/]+${COORD}`),
];

export function parseMapTarget(raw: string): MapTarget | null {
  const input = raw.trim();
  if (input === "") return null;
  for (const pattern of MAP_PATTERNS) {
    const m = input.match(pattern);
    if (!m) continue;
    const lat = Number(m[1]);
    const lon = Number(m[2]);
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) continue;
    if (lat < -90 || lat > 90 || lon < -180 || lon > 180) continue;
    return { lat, lon };
  }
  return null;
}

/**
 * The Google Maps share-link shape (ticket #674): a redirect short link
 * (https://maps.app.goo.gl/<id>) that carries no coordinates of its own
 * — it can never parse locally. Detection is the ONE home both the Map
 * form and the server's resolution action read, so what the form offers
 * to resolve is exactly what the endpoint accepts; the shape check is
 * also the action's hard precheck, which is what keeps that endpoint
 * from becoming a general URL-fetch primitive (https scheme, exactly
 * this host on its default port, exactly one opaque id segment —
 * anything else is refused before any network byte).
 */
export function isMapsShareLink(raw: string): boolean {
  const input = raw.trim();
  if (!input.startsWith("https://")) return false;
  try {
    const url = new URL(input);
    return (
      url.hostname === "maps.app.goo.gl" &&
      // The real shape rides the default port — no ":8443" variants.
      url.port === "" &&
      /^\/[A-Za-z0-9_-]+$/.test(url.pathname)
    );
  } catch {
    return false;
  }
}

/** The Map form's progress copy while a share link resolves (#674). */
export const MAP_SHARE_RESOLVING_MESSAGE = "Resolving maps link…";

/** The user-showable failure for a share link that cannot be resolved
 * (dead link, timeout, non-maps destination) — the server throws it and
 * every surface renders it through the shared error seam. */
export const MAP_SHARE_LINK_FAILED_MESSAGE =
  "Couldn't resolve that maps link — check it and try again";

/** The server-side refusal for input that is not a share link. The form
 * never sends such input (it runs the same detector first), so this is
 * the endpoint's defense against a drifted or hostile caller. */
export const MAP_SHARE_LINK_REFUSED_MESSAGE =
  "That link isn't a maps share link";

/** The iframe src a map embed renders (keyless OpenStreetMap embed). */
export function mapEmbedUrl(t: MapTarget, zoom = 15): string {
  const d = 0.72 / Math.pow(2, zoom / 8);
  const bbox = [
    t.lon - d,
    t.lat - d,
    t.lon + d,
    t.lat + d,
  ].map((n) => n.toFixed(5)).join("%2C");
  return `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${t.lat}%2C${t.lon}`;
}

/**
 * The iframe origins a note embed can render (ticket #652): YouTube's
 * privacy shell, Vimeo's player, and the keyless OpenStreetMap embed —
 * exactly what parseVideoUrl and mapEmbedUrl emit, never the input
 * hosts they accept. The web CSP's frame-src carries the SAME origins
 * as its own literal (the builder loads in the vite config's plain-node
 * graph, where this package's extensionless source cannot resolve);
 * securityHeaders.test.ts byte-welds the two lists, so a fourth embed
 * provider must extend BOTH or the weld reds.
 */
export const EMBED_FRAME_ORIGINS = [
  "https://www.youtube-nocookie.com",
  "https://player.vimeo.com",
  "https://www.openstreetmap.org",
] as const;

/**
 * What a dropped link becomes (#673): the video, map or link-card
 * embed the drop seam inserts. `raw` is the trimmed dropped string —
 * the exact payload the video/map forms store — and the parsed halves
 * ride along so a caller (and the tests) can see the parse happened.
 */
export type EmbedTargetClassification =
  | { kind: "embedVideo"; raw: string; video: VideoEmbed }
  | { kind: "embedMap"; raw: string; map: MapTarget }
  | { kind: "embedLink"; raw: string; url: string };

/**
 * The one link→embed classifier a dropped link rides (#673): video
 * first, then map, then the plain link card — that ORDER is load-
 * bearing, because every http(s) URL parses as a link, so the link
 * fallback must judge LAST. null = not an embed shape (a non-http(s)
 * scheme, or plain text that parses as nothing): the caller keeps its
 * default non-embed behavior. No second parsing lives here — the
 * FormatBar's own three parsers judge; this adds only the ORDER the
 * toolbar's forms never needed (a form knows its kind, a drop must
 * derive it).
 */
export function classifyEmbedTarget(
  raw: string,
): EmbedTargetClassification | null {
  const input = raw.trim();
  if (input === "") return null;
  const video = parseVideoUrl(input);
  if (video) return { kind: "embedVideo", raw: input, video };
  const map = parseMapTarget(input);
  if (map) return { kind: "embedMap", raw: input, map };
  const link = parseLinkTarget(input);
  if (link) return { kind: "embedLink", raw: input, url: link.url };
  return null;
}

/**
 * The dropped drag's link candidate (#673): the uri-list's first URL
 * line when the drag carries one (comment lines and blanks skipped —
 * browsers emit multi-line lists), else the plain-text payload. The
 * answer may still be non-URL text — the CLASSIFIER decides link vs
 * text, so a plain-text drop no parser accepts keeps flowing to the
 * editor's own text-drop behavior.
 */
export function dropLinkCandidate(
  uriList: string,
  textPlain: string,
): string | null {
  const fromList = uriList
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line !== "" && !line.startsWith("#"));
  if (fromList[0] !== undefined) return fromList[0];
  const text = textPlain.trim();
  return text === "" ? null : text;
}

export * from "./appearance";

export * from "./notesIsland";

export * from "./recurrence";

export * from "./ics";

export * from "./floatingTime";

/**
 * The one device-zone reader (issue #656): the timezone both apps'
 * export flow and the mobile reminder planner read the wall clock
 * through. Its react-consuming sibling — the export-arming flow —
 * deliberately does NOT ride this index (the server graph imports the
 * package root; react must never enter it): it rides the package's
 * "./calendarExportFlow" subpath export.
 */
export * from "./deviceTimeZone";


export * from "./notifications";

export * from "./push";

export * from "./errors";

export * from "./rateLimits";

export * from "./presence";


export * from "./verification";

export * from "./retention";

export * from "./inviteRole";

export * from "./searchText";

/**
 * The calendar's pure geometry and seams (tickets #213/#214, hoisted
 * from the mobile pair by #216 so the web port consumes the ONE
 * implementation — the "second consumer" this package exists for).
 */
export * from "./monthMatrix";

export * from "./dayPanel";

export * from "./density";

export * from "./lastKnownOverdue";

export * from "./listScope";

/**
 * Link preview metadata (ticket #675): extraction, the SSRF host law
 * and the stored-doc validation — the pure half of the server fetch,
 * with the action (convex/linkPreview.ts) as its thin transport.
 */
export * from "./linkPreview";

/**
 * The per-List Item quota (issue #654): the plan ceilings and the one
 * user-showable refusal copy, shared so the server throws and both
 * apps render the same bytes.
 */
export * from "./quotas";

/**
 * The pull-to-refresh gesture vocabulary (#756, home promoted to this
 * package by #758 so the notes island — a web page — consumes the same
 * constants and decisions as the native control; the gesture feels
 * identical on every surface).
 */
export * from "./pullRefresh";
